import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastService } from '@libs/shared-services';
import { authActions, selectIsUserLoggedIn } from '@libs/store';
import { ffdNotesRoutePrefix, routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { getFfdNotesInvitationErrorKey } from '../tokens/ffd-notes-invitation-error.util';
import { FfdNotesInvitationResolveService } from '../tokens/ffd-notes-invitation-resolve.service';

export const ffdNotesInvitationJoinRequestGuard: CanActivateFn = (route, state) => {
  const joinRequestId = route.queryParamMap.get('joinRequestId');
  if (!joinRequestId) {
    return true;
  }

  const store = inject(Store);
  const router = inject(Router);
  const invitationResolveService = inject(FfdNotesInvitationResolveService);
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);

  const requireLogin$ = (): Observable<boolean | ReturnType<Router['createUrlTree']>> => {
    store.dispatch(authActions.onProfileRequested());

    return store.select(selectIsUserLoggedIn).pipe(
      filter((isLoggedIn): isLoggedIn is boolean => isLoggedIn !== null),
      take(1),
      map(
        (isLoggedIn) =>
          isLoggedIn || router.createUrlTree([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl: state.url } }),
      ),
    );
  };

  // Skip the API call if we already resolved this exact join request in the same session
  if (invitationResolveService.joinRequestResolutionSignal() && invitationResolveService.joinRequestIdSignal() === joinRequestId) {
    return requireLogin$();
  }

  return invitationResolveService.resolveJoinRequest$(joinRequestId).pipe(
    switchMap(() => requireLogin$()),
    catchError((error) => {
      toastService.error(translateService.instant(getFfdNotesInvitationErrorKey(error)));
      return of(router.createUrlTree([...ffdNotesRoutePrefix(), routes.login]));
    }),
  );
};
